// ─────────────────────────────────────────────────────────────────────────────
// stores/session.ts – Reine Sitzungs-Logik (ohne Firebase-Abhaengigkeiten)
//
// Ausgelagerte, deterministische Kernlogik der 12-Stunden-Session (`pf_login_at`),
// damit die Ablauf-Entscheidung isoliert testbar ist (siehe test/session.test.ts).
// Verwendet von authStore.init() und authStore.checkSessionExpiry().
// ─────────────────────────────────────────────────────────────────────────────

/** Maximale Sitzungsdauer: 12 Stunden in Millisekunden. */
export const SESSION_MAX_MS = 12 * 60 * 60 * 1000;

/**
 * Prueft, ob eine gespeicherte Sitzung abgelaufen ist.
 *
 * Gibt `true` zurueck, wenn ein Zeitstempel vorliegt und aelter als 12 Stunden
 * ist. Fehlt der Zeitstempel (`null`), gilt die Sitzung an dieser Stelle NICHT
 * als abgelaufen (der erste Login setzt den Zeitstempel erst; die Behandlung des
 * fehlenden Werts liegt beim Aufrufer).
 *
 * @param storedAt  Roh-Wert aus localStorage (`pf_login_at`) oder `null`.
 * @param now       Aktueller Zeitpunkt in ms (Standard: `Date.now()`), injizierbar fuer Tests.
 */
export function isSessionExpired(storedAt: string | null, now: number = Date.now()): boolean {
  if (!storedAt) return false;
  return now - Number(storedAt) > SESSION_MAX_MS;
}

/**
 * Das Alter der gespeicherten Sitzung in Millisekunden — **allein fuer die
 * Diagnose** (T-M21.3, REQ-OPS-056 (c), REQ-OPS-055 (b)).
 *
 * ⚠ **Diese Funktion entscheidet nichts.** Der Ablauf-Entscheid liegt
 * unveraendert bei `isSessionExpired`; wer hier eine Grenze einbaut, hat zwei
 * Entscheidungsstellen statt einer.
 *
 * Rueckgabe `null` heisst: **es gibt kein lesbares Alter.** Das trifft auf zwei
 * verschiedene Faelle zu — kein Zeitstempel (`null`) und ein unlesbarer
 * Zeitstempel (etwa von Hand verstellt). Sie sind in der Protokollzeile
 * trotzdem auseinanderzuhalten, weil dort **neben** dem Alter der **gelesene
 * Rohwert** (`loginAt`) steht: `loginAt: null` gegen `loginAt: 'abc'`.
 *
 * @param storedAt  Roh-Wert aus localStorage (`pf_login_at`) oder `null`.
 * @param now       Aktueller Zeitpunkt in ms (Standard: `Date.now()`), injizierbar fuer Tests.
 */
export function sessionAgeMs(storedAt: string | null, now: number = Date.now()): number | null {
  if (!storedAt) return null;
  const begonnenAt = Number(storedAt);
  if (!Number.isFinite(begonnenAt)) return null;
  return now - begonnenAt;
}
